import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Facebook, Instagram, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { ScrollContext } from "@/contexts/ScrollContext";

// Section indices mapping - must match Index.tsx order
const SECTIONS = {
  HERO: 0,
  PAIN: 1,
  EXPERTISE: 2,
  PROCESS: 3,
  EXPERIENCE: 4,
  WHY: 5,
  TESTIMONIALS: 6,
  PORTFOLIO: 7,
  CTA: 8,
};

const Footer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const { toast } = useToast();
  const scrollContext = useContext(ScrollContext);
  const goToSection = scrollContext?.goToSection;

  const scrollToSection = (sectionIndex: number) => {
    if (goToSection) {
      goToSection(sectionIndex);
    }
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      toast({
        title: t("footer.subscribeSuccess"),
        description: t("footer.subscribeDesc"),
      });
      setEmail("");
    }
  };

  return (
    <footer className="bg-secondary py-16 px-6 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-20" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="text-2xl font-black">SHAPE</h3>
            <p className="text-sm text-muted-foreground">
              {t("footer.tagline")}
            </p>
            <div className="flex gap-3 pt-4">
              <a
                href="#"
                className="w-12 h-12 rounded-full glass-card glass-border flex items-center justify-center hover:text-[hsl(var(--holographic-cyan))] transition-all glow-on-hover"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-12 h-12 rounded-full glass-card glass-border flex items-center justify-center hover:text-[hsl(var(--holographic-pink))] transition-all glow-on-hover"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="font-bold">{t("footer.quickLinks")}</h4>
            <nav className="flex flex-col space-y-2">
              <button 
                onClick={() => scrollToSection(SECTIONS.EXPERTISE)} 
                className="text-sm text-muted-foreground hover:text-[hsl(var(--holographic-cyan))] transition-colors text-left"
              >
                Our Expertise
              </button>
              <button 
                onClick={() => scrollToSection(SECTIONS.PROCESS)} 
                className="text-sm text-muted-foreground hover:text-[hsl(var(--holographic-cyan))] transition-colors text-left"
              >
                Process
              </button>
              <button 
                onClick={() => scrollToSection(SECTIONS.PORTFOLIO)} 
                className="text-sm text-muted-foreground hover:text-[hsl(var(--holographic-cyan))] transition-colors text-left"
              >
                Work Examples
              </button>
              <button 
                onClick={() => scrollToSection(SECTIONS.CTA)} 
                className="text-sm text-muted-foreground hover:text-[hsl(var(--holographic-cyan))] transition-colors text-left"
              >
                Contact
              </button>
              <button 
                onClick={() => navigate("/courses")} 
                className="text-sm text-muted-foreground hover:text-[hsl(var(--holographic-cyan))] transition-colors text-left"
              >
                Courses
              </button>
              <button 
                onClick={() => navigate("/auth")} 
                className="text-sm text-muted-foreground hover:text-[hsl(var(--holographic-cyan))] transition-colors text-left"
              >
                Log In
              </button>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="font-bold">{t("footer.contact")}</h4>
            <a
              href="mailto:info@shapeagency.lt"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[hsl(var(--holographic-cyan))] transition-colors"
            >
              <Mail className="w-4 h-4" />
              info@shapeagency.lt
            </a>
          </div>

          {/* Newsletter */}
          <div className="space-y-4">
            <h4 className="font-bold">{t("footer.stayUpdated")}</h4>
            <p className="text-sm text-muted-foreground">
              {t("footer.getLatest")}
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <Input
                type="email"
                placeholder={t("footer.yourEmail")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="glass-card border-[hsl(var(--holographic-cyan))]/30 focus:border-[hsl(var(--holographic-cyan))]"
                required
              />
              <Button 
                type="submit" 
                size="icon"
                variant="glass"
                className="shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <p>{t("footer.copyright")}</p>
          <p className="text-xs">{t("footer.trustedBy")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
